import _ from '@sparta.global.vendors/lodash';

/**
 * @name        PerformanceMetaUtility
 * @file        performance-meta-utility.js
 * @description This utility adds performance meta at run time
 * @author      zkymyru
 */

const META_ATTR_CLASS = {
  prefetch: '.spa-prefetch',
  prerender: '.spa-prerender',
  preconnect: '.spa-preconnect',
  dnsPrefetch: '.spa-dns-prefetch'
};
const VALID_META_TAGS = {
  meta: 'meta',
  link: 'link'
};
const VALID_HTML_TAGS = {
  link: 'A'
};

/**
 * Adds performance meta to head section
 */
class PerformanceMetaUtility {
  /**
   * Search and handle all elements with performance classes
   *
   * @public
   * @testExample addMetaFromClasses()
   * @testOutput undefined
   * @testExpect document.querySelector('link[href="http://localhost/"][rel="prefetch"]') toExist
   * @testExpect document.querySelector('link[href="http://localhost/"][rel="preconnect"]') toExist
   * @testExpect document.querySelector('link[href="http://localhost/"][crossorigin]') toExist
   * @mockElement a.spa-prefetch.spa-preconnect.spa-crossorigin[href="http://localhost/"]
   * @mockDelay 1000
   * @returns {void}
   */
  addMetaFromClasses() {
    if (spaParams.siteParams.disablePerformanceMeta) return; // Chicken switch
    if (!this.fragment) {
      this.fragment = document.createDocumentFragment();
    }
    let isAppended = false;
    this.getCurrentMeta();
    _.forOwn(META_ATTR_CLASS, (className, relAttr) => {
      const relAttrValue = _.kebabCase(relAttr);
      const elements = [...document.querySelectorAll(className)];
      if (elements.length > 0) {
        elements.forEach(element => {
          if (element.href && !PerformanceMetaUtility.isHidden(element) && element.tagName === VALID_HTML_TAGS.link) {
            const attrs = {
              rel: relAttrValue,
              href: element.href
            };
            if (element.classList.contains('spa-crossorigin')) attrs.crossorigin = '';
            if (this.appendElementToFragment(VALID_META_TAGS.link, attrs)) {
              isAppended = true;
            }
          }
        });
      }
    });
    // call only if new elements were appended to fragment
    if (isAppended) this.scheduleFragmentAppend();
  }

  /**
   * Add performance meta based on object parameter at run time
   * @public
   * @param {object} tags - object for meta construction
   * @testExample
   *   addMetaFromObject({
   *     meta: [{ 'http-equiv': 'x-dns-prefetch-control', 'content': 'on' }],
   *     link: [
   *       { rel: 'preconnect', href: 'http://localhost.obj/' },
   *       { rel: 'prefetch', href: 'http://localhost.obj/' },
   *     ],
   *   })
   * @testException addMetaFromObject('invalid')
   * @testOutput undefined
   * @testExpect document.querySelector('link[href="http://localhost.obj/"][rel="prefetch"]') toExist
   * @testExpect document.querySelector('link[href="http://localhost.obj/"][rel="preconnect"]') toExist
   * @mockDelay 1000
   * @returns {void}
   */
  addMetaFromObject(tags) {
    if (spaParams.siteParams.disablePerformanceMeta) return; // Chicken switch
    if (tags && typeof tags === 'object') {
      if (!this.fragment) {
        this.fragment = document.createDocumentFragment();
      }
      let isAppended = false;
      this.getCurrentMeta();
      Object.keys(tags).forEach(tagType => {
        if (PerformanceMetaUtility.isValidMetaTag(tagType)) {
          tags[tagType].forEach(tag => {
            if (this.appendElementToFragment(tagType, tag)) {
              isAppended = true;
            }
          });
        }
      });
      // call only if new elements were appended to fragment
      if (isAppended) this.scheduleFragmentAppend();
    } else {
      throw new Error(`PerformanceMetaUtility.addMetaFromObject()
           expects its first parameter to be an object`);
    }
  }

  /**
   * Gets current meta and link tags from head section
   * @returns {void}
   */
  getCurrentMeta() {
    const headSelector = document.querySelector('head');
    this.allHeaderLinks = headSelector.querySelectorAll('link');
    this.allMetaLinks = headSelector.querySelectorAll('meta[http-equiv]');
  }

  /**
   * Appends new elements to fragment
   * @private
   * @param {string} tagType ['link'|'meta'] - meta tag type
   * @param {object} attributes - attributes of the meta we're trying to add
   * @returns {boolean} true if element was appended
   */
  appendElementToFragment(tagType, attributes) {
    const metaElement = this.createMetaElement(tagType, attributes);
    const fragmentElements = this.fragment.querySelectorAll('link');
    // add without duplicates
    if (metaElement && !this.doAttributesEqual(fragmentElements, attributes)) {
      this.fragment.appendChild(metaElement);
      return true;
    }
    return false;
  }

  /**
   * Iterates through all existing meta tags and checks if all attributes equal
   * @private
   * @param {NodeList} meta - existing meta or link elements in the head section
   * @param {Object} attributes - attributes we're checking
   * @return {boolean} true if attributes equal
   */
  doAttributesEqual(meta, attributes) {
    return [...meta].some(link => {
      const headerTags = {};
      [...link.attributes].forEach(item => {
        headerTags[item.name] = item.value;
      });
      return _.isEqual(headerTags, attributes);
    });
  }

  /**
   * Checks if performance meta is already in the head section
   * @private
   * @param {string} tagType - meta tag type
   * @param {object} attributes - attributes of the meta we're trying to add
   * @returns {boolean} true if meta tag with the same attributes already exists
   */
  isMetaAlreadyInHead(tagType, attributes) {
    if (tagType === VALID_META_TAGS.meta) {
      return this.doAttributesEqual(this.allMetaLinks, attributes);
    }
    return this.doAttributesEqual(this.allHeaderLinks, attributes);
  }

  /**
   * Checks if element is hidden
   * @private
   * @param {Element} element - DOM element
   * @returns {boolean} true if hidden
   */
  static isHidden(element) {
    const style = getComputedStyle(element);
    return style.display === 'none' || style.visibility === 'hidden';
  }

  /**
   * Appends performance meta to the head section
   * @private
   * @param {string} tagType ['link'|'meta'] - meta tag type
   * @param {object} attributes - object with meta tag attributes
   * @returns {Node | undefined} created element or undefined
   */
  createMetaElement(tagType, attributes) {
    if (!this.isMetaAlreadyInHead(tagType, attributes)) {
      const element = document.createElement(tagType);
      _.forOwn(attributes, (value, property) => {
        element.setAttribute(property, value);
      });
      return element;
    }
    return undefined;
  }

  /**
   * Schedules fragment appending to next requestAnimationFrame callback
   * @private
   * @returns {void}
   */
  scheduleFragmentAppend() {
    if (this.fragment.children.length > 0) {
      // Don't append to the document immediately, wait for the next
      // requestAnimationFrame callback
      requestAnimationFrame(() => {
        document.head.appendChild(this.fragment);
      });
    }
  }

  /**
   * Checks if meta tag is valid
   * @private
   * @param {string} tagType - meta tag
   * @returns {boolean} - true if valid
   */
  static isValidMetaTag(tagType) {
    return Object.prototype.hasOwnProperty.call(VALID_META_TAGS, tagType);
  }
}
var index = new PerformanceMetaUtility();

export { index as default };
